import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  FaArrowRight,
  FaChartLine,
  FaGift,
  FaShieldAlt,
  FaClock,
  FaTrophy,
  FaStar,
  FaCheckCircle,
  FaFutbol,
  FaBasketballBall,
  FaTableTennis,
  FaHockeyPuck,
  FaVolleyballBall,
  FaBaseballBall,
  FaMobileAlt,
  FaCreditCard,
  FaQuestionCircle,
  FaNewspaper,
  FaUsers,
  FaRocket,
  FaAward,
  FaTimes,
} from 'react-icons/fa';

interface NewsItem {
  title: string;
  date: string;
  excerpt: string;
  content: string;
}

const Home: React.FC = () => {
  const [scrollY, setScrollY] = useState(0);
  const [selectedNews, setSelectedNews] = useState<number | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  const newsItems: NewsItem[] = [
    {
      title: 'Nouveaux bonus disponibles en décembre',
      date: '3 décembre 2025',
      excerpt: 'Découvrez les dernières offres promotionnelles des bookmakers pour ce mois.',
      content: `Les bookmakers français proposent actuellement des bonus exceptionnels pour le mois de décembre. Bwin offre un bonus de bienvenue jusqu'à 100€, tandis qu'Unibet propose une promotion spéciale de 150€ pour les nouveaux joueurs. Vbet n'est pas en reste avec son offre de 120€ accompagnée d'un programme VIP exclusif.

Ces promotions sont limitées dans le temps et soumises à des conditions de mise. Nous vous recommandons de consulter les conditions générales de chaque bookmaker avant de vous inscrire. Profitez de ces offres avant qu'elles ne disparaissent !`,
    },
    {
      title: 'Meilleures cotes du week-end',
      date: '1 décembre 2025',
      excerpt: 'Analyse des meilleures cotes disponibles pour les matchs de ce week-end.',
      content: `Ce week-end s'annonce riche en événements sportifs majeurs. Notre analyse comparative révèle que les cotes les plus attractives se trouvent sur plusieurs bookmakers pour différents matchs.

Pour le football, Bwin propose actuellement les meilleures cotes sur les matchs de Ligue 1, avec un avantage moyen de 2-3% par rapport à la concurrence. Unibet se distingue sur les paris combinés avec des multiplicateurs intéressants.

N'oubliez pas de comparer les cotes avant chaque pari pour maximiser vos gains potentiels. Utilisez notre outil de comparaison pour identifier rapidement les meilleures opportunités.`,
    },
    {
      title: 'Nouvelle réglementation ANJ',
      date: '28 novembre 2025',
      excerpt: 'Les dernières mises à jour de la réglementation des paris en ligne en France.',
      content: `L'Autorité Nationale des Jeux (ANJ) a annoncé de nouvelles mesures de protection des joueurs qui entreront en vigueur en janvier 2026. Ces réglementations renforcent les obligations des bookmakers en matière de jeu responsable.

Parmi les principales nouveautés :
- Limites de dépôt renforcées pour les nouveaux comptes
- Obligation d'affichage plus visible des messages de prévention
- Amélioration des outils d'auto-exclusion
- Renforcement des contrôles d'identité

Tous les bookmakers licenciés devront s'adapter à ces nouvelles règles. Cette évolution renforce la sécurité et la transparence du marché français des paris en ligne.`,
    },
  ];

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const offers = [
    {
      id: 'bwin',
      name: 'Bwin',
      bonus: '100€',
      image: '/images/offers/bwin.webp',
      rating: 4.8,
      features: ['Bonus de bienvenue', 'Cotes compétitives', 'Cashback'],
      url: 'https://bwin.fr/',
    },
    {
      id: 'unibet',
      name: 'Unibet',
      bonus: '150€',
      image: '/images/offers/unibet.webp',
      rating: 4.9,
      features: ['Pari gratuit', 'Streaming live', 'App mobile'],
      url: 'https://www.unibet.fr/',
    },
    {
      id: 'vbet',
      name: 'Vbet',
      bonus: '120€',
      image: '/images/offers/vbet.webp',
      rating: 4.7,
      features: ['Pari combiné', 'VIP program', 'Retraits rapides'],
      url: 'https://vbet.fr/',
    },
  ];

  const stats = [
    { icon: FaChartLine, value: '50+', label: 'Bookmakers analysés' },
    { icon: FaGift, value: '200€+', label: 'Bonus moyens' },
    { icon: FaShieldAlt, value: '100%', label: 'Sécurisé' },
    { icon: FaClock, value: '24/7', label: 'Support' },
  ];

  const features = [
    {
      icon: FaTrophy,
      title: 'Meilleures cotes',
      description: 'Comparaison en temps réel des meilleures cotes du marché français.',
    },
    {
      icon: FaGift,
      title: 'Bonus exclusifs',
      description: 'Accès à des offres promotionnelles réservées à nos utilisateurs.',
    },
    {
      icon: FaShieldAlt,
      title: 'Sites vérifiés',
      description: 'Tous les bookmakers sont licenciés et régulés par l\'ANJ.',
    },
    {
      icon: FaChartLine,
      title: 'Analyses détaillées',
      description: 'Graphiques et statistiques pour prendre les meilleures décisions.',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'BetSprinto',
    description:
      'Guide expert des meilleures offres de paris sportifs en France. Comparaison, avis et analyses détaillées.',
    url: 'https://betsprinto.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://betsprinto.com/recherche?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <SEO
        title="Meilleures Offres de Paris Sportifs en France 2025"
        description="Découvrez les meilleures offres de paris sportifs en France. Comparaisons détaillées, bonus exclusifs et analyses expertes des bookmakers licenciés."
        jsonLd={jsonLd}
      />

      {/* Hero Section with 6 Anchor Links */}
      <section
        ref={heroRef}
        id="hero"
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-28"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(/images/heromain.webp)',
            transform: `translateY(${scrollY * 0.5}px)`,
            willChange: 'transform',
          }}
        />
        
        <div className="relative z-10 container mx-auto px-4 py-20 text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
            <span className="text-gradient">Les Meilleures Offres</span>
            <br />
            <span className="text-white">de Paris Sportifs</span>
          </h1>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto animate-slide-up">
            Comparez les meilleurs bookmakers français, profitez de bonus exclusifs et pariez en
            toute sécurité sur vos sports favoris.
          </p>

          {/* 6 Anchor Links */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto mb-12">
            <a
              href="#offres"
              className="glass p-4 rounded-lg hover:bg-dark-hover transition-all group"
            >
              <FaGift className="text-accent-primary mx-auto mb-2 group-hover:scale-110 transition-transform" size={24} />
              <span className="text-sm text-gray-300">Offres</span>
            </a>
            <a
              href="#sports"
              className="glass p-4 rounded-lg hover:bg-dark-hover transition-all group"
            >
              <FaFutbol className="text-accent-secondary mx-auto mb-2 group-hover:scale-110 transition-transform" size={24} />
              <span className="text-sm text-gray-300">Sports</span>
            </a>
            <a
              href="#comment-ca-marche"
              className="glass p-4 rounded-lg hover:bg-dark-hover transition-all group"
            >
              <FaRocket className="text-accent-success mx-auto mb-2 group-hover:scale-110 transition-transform" size={24} />
              <span className="text-sm text-gray-300">Comment ça marche</span>
            </a>
            <a
              href="#mobile"
              className="glass p-4 rounded-lg hover:bg-dark-hover transition-all group"
            >
              <FaMobileAlt className="text-accent-primary mx-auto mb-2 group-hover:scale-110 transition-transform" size={24} />
              <span className="text-sm text-gray-300">Mobile</span>
            </a>
            <a
              href="#temoignages"
              className="glass p-4 rounded-lg hover:bg-dark-hover transition-all group"
            >
              <FaUsers className="text-accent-secondary mx-auto mb-2 group-hover:scale-110 transition-transform" size={24} />
              <span className="text-sm text-gray-300">Témoignages</span>
            </a>
            <a
              href="#faq-preview"
              className="glass p-4 rounded-lg hover:bg-dark-hover transition-all group"
            >
              <FaQuestionCircle className="text-accent-success mx-auto mb-2 group-hover:scale-110 transition-transform" size={24} />
              <span className="text-sm text-gray-300">FAQ</span>
            </a>
          </div>

          <Link
            to="/comparaison"
            className="inline-flex items-center space-x-2 bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-4 px-8 rounded-lg transition-all text-lg"
          >
            <span>Comparer les offres</span>
            <FaArrowRight />
          </Link>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="text-center glass p-6 rounded-xl hover:bg-dark-hover transition-all animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <stat.icon className="text-accent-primary mx-auto mb-4" size={40} />
                <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offers Section */}
      <section id="offres" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            <span className="text-gradient">Top Offres</span> du Moment
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Sélection des meilleures promotions disponibles actuellement sur le marché français.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {offers.map((offer, index) => (
              <div
                key={offer.id}
                className="glass rounded-xl overflow-hidden hover:bg-dark-hover transition-all group animate-slide-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-48 bg-dark-card">
                  <img
                    src={offer.image}
                    alt={offer.name}
                    className="w-full h-full object-contain p-4"
                    loading="lazy"
                    width="300"
                    height="200"
                  />
                  <div className="absolute top-4 right-4 bg-accent-primary text-white px-3 py-1 rounded-full text-sm font-bold">
                    {offer.bonus}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold text-white">{offer.name}</h3>
                    <div className="flex items-center space-x-1">
                      <FaStar className="text-yellow-400" size={16} />
                      <span className="text-white font-semibold">{offer.rating}</span>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {offer.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-gray-300 text-sm">
                        <FaCheckCircle className="text-accent-success" size={14} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to={`/offre/${offer.id}`}
                      className="flex-1 bg-dark-card hover:bg-dark-hover text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center space-x-2 text-sm"
                    >
                      <span>Voir l'offre</span>
                    </Link>
                    <a
                      href={offer.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-3 px-6 rounded-lg transition-all flex items-center justify-center space-x-2 group-hover:scale-105 text-sm"
                    >
                      <span>Visiter le site</span>
                      <FaArrowRight size={12} />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="avantages" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            Pourquoi Choisir <span className="text-gradient">BetSprinto</span>?
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Nous vous aidons à trouver les meilleures offres et à parier en toute confiance.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="glass p-6 rounded-xl hover:bg-dark-hover transition-all group"
              >
                <feature.icon className="text-accent-primary mb-4 group-hover:scale-110 transition-transform" size={32} />
                <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Preview */}
      <section id="comparaison" className="py-20">
        <div className="container mx-auto px-4">
          <div className="glass rounded-2xl p-8 md:p-12">
            <h2 className="text-4xl font-bold mb-4 text-center">
              Comparaison <span className="text-gradient">Interactive</span>
            </h2>
            <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">
              Comparez facilement les bookmakers sur différents critères importants.
            </p>
            <div className="flex justify-center">
              <Link
                to="/comparaison"
                className="inline-flex items-center space-x-2 bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-4 px-8 rounded-lg transition-all"
              >
                <span>Comparer maintenant</span>
                <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="securite" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <FaShieldAlt className="text-accent-primary mx-auto mb-6" size={64} />
            <h2 className="text-4xl font-bold mb-4">
              Sécurité et <span className="text-gradient">Confiance</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Tous les bookmakers présentés sont licenciés par l'Autorité Nationale des Jeux (ANJ)
              et respectent les normes strictes de sécurité et de protection des joueurs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://anj.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="glass px-6 py-3 rounded-lg hover:bg-dark-hover transition-all"
                aria-label="ANJ - Autorité Nationale des Jeux"
              >
                <img src="/images/footer/anj.webp" alt="ANJ" className="h-8" loading="lazy" />
              </a>
              <a
                href="https://www.hippodromesenfete.fr/jouons-responsable/"
                target="_blank"
                rel="noopener noreferrer"
                className="glass px-6 py-3 rounded-lg hover:bg-dark-hover transition-all"
                aria-label="Jouons Responsable"
              >
                <img
                  src="/images/footer/jouns.webp"
                  alt="Jouons Responsable"
                  className="h-8"
                  loading="lazy"
                />
              </a>
              <a
                href="https://www.data.gouv.fr/en/organizations/autorite-de-regulation-des-jeux-en-ligne-arjel/"
                target="_blank"
                rel="noopener noreferrer"
                className="glass px-6 py-3 rounded-lg hover:bg-dark-hover transition-all"
                aria-label="ARJEL"
              >
                <img src="/images/footer/arjel.webp" alt="ARJEL" className="h-8" loading="lazy" />
              </a>
              <a
                href="https://mediateurdesjeuxenligne.fr/"
                target="_blank"
                rel="noopener noreferrer"
                className="glass px-6 py-3 rounded-lg hover:bg-dark-hover transition-all"
                aria-label="Médiateur des Jeux en Ligne"
              >
                <img
                  src="/images/footer/mediater.webp"
                  alt="Médiateur des Jeux en Ligne"
                  className="h-8"
                  loading="lazy"
                />
              </a>
              <a
                href="https://www.gambleaware.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="glass px-6 py-3 rounded-lg hover:bg-dark-hover transition-all"
                aria-label="BeGambleAware"
              >
                <img
                  src="/images/footer/begamble.webp"
                  alt="BeGambleAware"
                  className="h-8"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="avis" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">
            Avis <span className="text-gradient">Experts</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, j) => (
                    <FaStar key={j} className="text-yellow-400" size={16} />
                  ))}
                </div>
                <p className="text-gray-300 mb-4">
                  "Plateforme excellente pour comparer les offres. Les analyses sont détaillées et
                  m'ont aidé à faire le bon choix."
                </p>
                <div className="text-gray-400 text-sm">- Utilisateur vérifié</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sports Section */}
      <section id="sports" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            Sports <span className="text-gradient">Populaires</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Pariez sur vos sports favoris avec les meilleures cotes du marché français.
          </p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-6">
            {[
              { icon: FaFutbol, name: 'Football', color: 'text-green-400' },
              { icon: FaBasketballBall, name: 'Basket', color: 'text-orange-400' },
              { icon: FaTableTennis, name: 'Tennis', color: 'text-yellow-400' },
              { icon: FaHockeyPuck, name: 'Hockey', color: 'text-blue-400' },
              { icon: FaVolleyballBall, name: 'Volley', color: 'text-red-400' },
              { icon: FaBaseballBall, name: 'Baseball', color: 'text-purple-400' },
            ].map((sport, index) => (
              <div
                key={index}
                className="glass p-6 rounded-xl text-center hover:bg-dark-hover transition-all group cursor-pointer"
              >
                <sport.icon className={`${sport.color} mx-auto mb-3 group-hover:scale-110 transition-transform`} size={32} />
                <div className="text-white font-semibold text-sm">{sport.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="comment-ca-marche" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            Comment ça <span className="text-gradient">Marche</span>?
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            En quelques étapes simples, trouvez le bookmaker parfait pour vos paris.
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                icon: FaChartLine,
                title: 'Comparez',
                description: 'Analysez les offres et bonus de chaque bookmaker',
              },
              {
                step: '2',
                icon: FaGift,
                title: 'Choisissez',
                description: 'Sélectionnez l\'offre qui correspond à vos besoins',
              },
              {
                step: '3',
                icon: FaRocket,
                title: 'Inscrivez-vous',
                description: 'Créez votre compte et profitez du bonus de bienvenue',
              },
              {
                step: '4',
                icon: FaTrophy,
                title: 'Pariez',
                description: 'Commencez à parier avec les meilleures conditions',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="glass p-6 rounded-xl text-center hover:bg-dark-hover transition-all relative"
              >
                <div className="absolute -top-4 -right-4 bg-accent-primary text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">
                  {item.step}
                </div>
                <item.icon className="text-accent-primary mx-auto mb-4" size={40} />
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section id="paiements" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            Méthodes de <span className="text-gradient">Paiement</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Dépôts et retraits sécurisés avec les moyens de paiement les plus populaires.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              'Carte bancaire',
              'PayPal',
              'Virement bancaire',
              'Skrill',
              'Neteller',
              'Crypto',
            ].map((method, index) => (
              <div
                key={index}
                className="glass p-6 rounded-xl text-center hover:bg-dark-hover transition-all"
              >
                <FaCreditCard className="text-accent-primary mx-auto mb-3" size={32} />
                <div className="text-white font-semibold text-sm">{method}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Apps Section */}
      <section id="mobile" className="py-12">
        <div className="container mx-auto px-4">
          <div className="glass rounded-xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <FaMobileAlt className="text-accent-primary flex-shrink-0" size={48} />
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">
                  Applications <span className="text-gradient">Mobiles</span>
                </h2>
                <p className="text-gray-300 text-sm mb-4">
                  Pariez où que vous soyez avec les applications mobiles disponibles sur iOS et Android.
                </p>
                <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                  <div className="bg-dark-card px-3 py-1.5 rounded-lg">
                    <div className="text-xs text-gray-400">App Store</div>
                  </div>
                  <div className="bg-dark-card px-3 py-1.5 rounded-lg">
                    <div className="text-xs text-gray-400">Google Play</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="temoignages" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            Témoignages <span className="text-gradient">Clients</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Découvrez ce que nos utilisateurs pensent de BetSprinto.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Marc D.',
                rating: 5,
                text: 'Grâce à BetSprinto, j\'ai trouvé le bookmaker avec les meilleures cotes. Le bonus était vraiment intéressant et les retraits sont rapides.',
                date: 'Il y a 2 semaines',
              },
              {
                name: 'Sophie L.',
                rating: 5,
                text: 'Comparaison très détaillée et analyses pertinentes. J\'apprécie particulièrement la section sécurité qui m\'a rassurée.',
                date: 'Il y a 1 mois',
              },
              {
                name: 'Thomas R.',
                rating: 5,
                text: 'Interface claire et informations à jour. J\'utilise régulièrement le site pour comparer les offres avant de parier.',
                date: 'Il y a 3 semaines',
              },
            ].map((testimonial, index) => (
              <div key={index} className="glass p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <div className="flex">
                    {[...Array(testimonial.rating)].map((_, j) => (
                      <FaStar key={j} className="text-yellow-400" size={16} />
                    ))}
                  </div>
                </div>
                <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold">{testimonial.name}</div>
                    <div className="text-gray-400 text-xs">{testimonial.date}</div>
                  </div>
                  <FaUsers className="text-accent-primary" size={24} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Preview Section */}
      <section id="faq-preview" className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-4xl font-bold text-center mb-4">
            Questions <span className="text-gradient">Fréquentes</span>
          </h2>
          <p className="text-gray-400 text-center mb-12">
            Trouvez rapidement des réponses aux questions les plus courantes.
          </p>
          <div className="space-y-4 mb-8">
            {[
              {
                question: 'Comment choisir le meilleur bookmaker ?',
                answer: 'Utilisez notre outil de comparaison pour identifier celui qui correspond le mieux à vos besoins en termes de cotes, bonus et fonctionnalités.',
              },
              {
                question: 'Les bonus sont-ils vraiment gratuits ?',
                answer: 'Les bonus de bienvenue sont offerts lors de votre premier dépôt, mais soumis à des conditions de mise (rollover).',
              },
              {
                question: 'Combien de temps pour retirer mes gains ?',
                answer: 'Les délais varient généralement entre 24 et 48 heures, certains bookmakers proposent des retraits express en moins de 24h.',
              },
            ].map((faq, index) => (
              <div key={index} className="glass p-6 rounded-xl">
                <h3 className="text-white font-semibold mb-2 flex items-center">
                  <FaQuestionCircle className="text-accent-primary mr-3" size={20} />
                  {faq.question}
                </h3>
                <p className="text-gray-400 text-sm ml-8">{faq.answer}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              to="/faq"
              className="inline-flex items-center space-x-2 text-accent-primary hover:text-accent-primary/80 transition-colors font-semibold"
            >
              <span>Voir toutes les questions</span>
              <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section id="actualites" className="py-20 bg-dark-surface">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            Actualités <span className="text-gradient">Paris Sportifs</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Restez informé des dernières nouveautés et promotions des bookmakers français.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {newsItems.map((news, index) => (
              <div
                key={index}
                onClick={() => setSelectedNews(index)}
                className="glass p-6 rounded-xl hover:bg-dark-hover transition-all group cursor-pointer"
              >
                <FaNewspaper className="text-accent-primary mb-4 group-hover:scale-110 transition-transform" size={32} />
                <div className="text-gray-400 text-xs mb-2">{news.date}</div>
                <h3 className="text-white font-bold text-lg mb-2 group-hover:text-accent-primary transition-colors">
                  {news.title}
                </h3>
                <p className="text-gray-400 text-sm">{news.excerpt}</p>
              </div>
            ))}
          </div>

          {/* News Modal */}
          {selectedNews !== null && (
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in p-4"
              onClick={() => setSelectedNews(null)}
            >
              <div
                className="glass rounded-2xl p-6 md:p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-slide-up"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center space-x-4 flex-1">
                    <FaNewspaper className="text-accent-primary flex-shrink-0" size={32} />
                    <div className="flex-1">
                      <div className="text-gray-400 text-sm mb-1">{newsItems[selectedNews].date}</div>
                      <h2 className="text-2xl md:text-3xl font-bold text-white">{newsItems[selectedNews].title}</h2>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedNews(null)}
                    className="text-gray-400 hover:text-white transition-colors flex-shrink-0 ml-4"
                    aria-label="Fermer"
                  >
                    <FaTimes size={24} />
                  </button>
                </div>
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                    {newsItems[selectedNews].content}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Awards Section */}
      <section id="recompenses" className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">
            Récompenses et <span className="text-gradient">Reconnaissances</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Les bookmakers que nous recommandons ont été récompensés pour leur excellence.
          </p>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: 'Meilleur Bonus', icon: FaAward, color: 'text-yellow-400' },
              { title: 'Meilleures Cotes', icon: FaChartLine, color: 'text-green-400' },
              { title: 'App Mobile', icon: FaMobileAlt, color: 'text-blue-400' },
              { title: 'Support Client', icon: FaUsers, color: 'text-purple-400' },
            ].map((award, index) => (
              <div
                key={index}
                className="glass p-6 rounded-xl text-center hover:bg-dark-hover transition-all group"
              >
                <award.icon className={`${award.color} mx-auto mb-4 group-hover:scale-110 transition-transform`} size={40} />
                <div className="text-white font-semibold">{award.title}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="cta" className="py-20 bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Prêt à Commencer ?
          </h2>
          <p className="text-gray-300 text-xl mb-8 max-w-2xl mx-auto">
            Comparez les meilleures offres et trouvez le bookmaker parfait pour vos paris sportifs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/comparaison"
              className="inline-flex items-center space-x-2 bg-accent-primary hover:bg-accent-primary/90 text-white font-semibold py-4 px-8 rounded-lg transition-all text-lg"
            >
              <span>Comparer les offres</span>
              <FaArrowRight />
            </Link>
            <Link
              to="/faq"
              className="inline-flex items-center space-x-2 glass text-white font-semibold py-4 px-8 rounded-lg transition-all text-lg hover:bg-dark-hover"
            >
              <FaQuestionCircle />
              <span>Voir la FAQ</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;

